import {
  configureStore,
  ThunkAction,
  Action,
  combineReducers,
  Reducer,
} from "@reduxjs/toolkit";
import authReducer from "redux/authSlice";
import appReducer from "redux/appSlice";
import preferenceReducer from "redux/preferencesSlice";
import featuresReducer from "redux/featuresSlice";
import inPersonSessionReducer, {
  pruneStaleInPersonSession,
} from "redux/inPersonSessionSlice";
import { historyMiddleware } from "redux/historyMiddleware";
import { mixpanelMiddleware } from "redux/mixpanelMiddleware";
import { IS_EMBED } from "embed/embedMode";
import { contractSearchMiddleware } from "redux/contractSearchMiddleware";

/**
 * The in-person session is the one slice NOT kept in localStorage. It holds a
 * live signing token and the walk-in signer's details, and the feature runs on
 * shared/kiosk devices, so it must not outlive the tab. sessionStorage is
 * scoped exactly that way and still survives the full-page navigations the
 * hand-off loop uses (`window.location.assign`), which is why the slice can't
 * simply live in memory.
 */
const IN_PERSON_STORAGE_KEY = "inPersonSession";

const loadState = () => {
  try {
    const loadedState = localStorage.getItem("state");
    const baseState = loadedState === null ? undefined : JSON.parse(loadedState);
    // A build from before this carve-out may have left a session inside the
    // localStorage blob; drop it rather than rehydrating a stale token. The
    // next save rewrites the blob without it.
    if (baseState) delete baseState.inPersonSession;
    const session = sessionStorage.getItem(IN_PERSON_STORAGE_KEY);
    if (session === null) return baseState;
    try {
      // Still expire on rehydrate: a tab parked overnight shouldn't resume a
      // session the host walked away from hours ago.
      const pruned = pruneStaleInPersonSession({
        ...(baseState ?? {}),
        inPersonSession: JSON.parse(session),
      });
      // Drop it from storage too. The next dispatch would clear it anyway, but
      // the TTL is only our policy — the token it holds may still be live
      // server-side, so don't leave it sitting there until then.
      if (!pruned.inPersonSession) {
        sessionStorage.removeItem(IN_PERSON_STORAGE_KEY);
      }
      return pruned;
    } catch (sessionError: any) {
      // A corrupt in-person entry must only cost the hand-off session — it must
      // not take auth/preferences down with it and log the user out. Log it, or
      // a session vanishing at boot is invisible to debug.
      console.warn(
        "Discarding unreadable in-person session:",
        sessionError?.message ?? sessionError,
      );
      sessionStorage.removeItem(IN_PERSON_STORAGE_KEY);
      return baseState;
    }
  } catch (error: any) {
    return undefined;
  }
};
// Embedded widgets (partner iframes) keep the store in memory only: third-party iframes get partitioned or
// blocked storage, and reading the persisted blob would leak this browser's own Inkfree login -- or the
// previous signer's state on a shared partner machine -- into the widget.
const persistedState = IS_EMBED ? undefined : loadState();

const combinedReducer = combineReducers({
  auth: authReducer,
  preference: preferenceReducer,
  app: appReducer,
  features: featuresReducer,
  inPersonSession: inPersonSessionReducer,
});

const rootReducer: Reducer = (state: RootState, action: any) => {
  if (action.type === "auth/logOutAction") {
    state = undefined;
  }
  return combinedReducer(state, action);
};

let quotaWarned = false;
const saveState = (state: RootState) => {
  const { inPersonSession, ...persistedSlices } = state;
  // Written first and in its own try: the localStorage write below routinely
  // hits the quota mid-signing (see below), and that must not cost the
  // hand-off session the only copy it has to survive the next navigation.
  try {
    if (inPersonSession?.active) {
      sessionStorage.setItem(
        IN_PERSON_STORAGE_KEY,
        JSON.stringify(inPersonSession),
      );
    } else {
      sessionStorage.removeItem(IN_PERSON_STORAGE_KEY);
    }
  } catch (error: any) {
    console.warn("Skipping in-person session persistence:", error);
  }
  try {
    localStorage.setItem("state", JSON.stringify(persistedSlices));
  } catch (error: any) {
    // localStorage has a ~5MB per-origin quota. Signature/initials images are
    // stored in-request as base64 data URLs and can push the persisted state
    // past that limit. When that happens we skip the write; the current
    // session keeps working from in-memory Redux state.
    if (!quotaWarned) {
      quotaWarned = true;
      console.warn("Skipping Redux state persistence:", error?.message ?? error);
    }
  }
};

export const store = configureStore({
  reducer: rootReducer,
  preloadedState: persistedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(historyMiddleware, mixpanelMiddleware, contractSearchMiddleware),
});

if (!IS_EMBED) {
  store.subscribe(() => {
    saveState(store.getState());
  });
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
